import { createContext, type ReactNode, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { useXmlConfiguratorTranslations } from "~/Configurators/common/useXmlConfiguratorTranslations";
import { Card } from "~/Configurators/Linear/components/Card";
import classes from "./LinearTileV2.module.less";
import { type LinearTileProps } from "~/Configurators/Linear/components/LinearTile";
import classNames from "classnames";
import LanguageAwareExternalContent from "~/Configurators/Linear/components/LanguageAwareExternalContent";
import LinearInfo from "~/Configurators/Linear/components/LinearInfo";
import { CheckedCircle } from "~/common/components/CheckedCircle/CheckedCircle";

type Props = Omit<LinearTileProps, "onClickHandler" | "isPicked" | "isDisabled" | "link"> & {
  onClick?: () => void;
  selected?: boolean;
  disabled?: boolean;
  href?: string;
  target?: JSX.IntrinsicElements["a"]["target"];
  style?: JSX.IntrinsicElements["div"]["style"];
};
export const LinearTileV2 = (props: Props) => {
  return <Base {...props} />;
};

const Base = ({
  selected,
  disabled,
  className,
  href,
  ...rest
}: Pick<
  Props,
  "onClick" | "selected" | "disabled" | "className" | "href" | "target" | "children" | "style" | "error"
>) => {
  return (
    <IsPickedContext.Provider value={selected}>
      <Card
        className={classNames(classes.tile, className)}
        disabled={disabled}
        active={selected}
        disableHover={disabled || (!rest.onClick && !href)}
        as={href ? "a" : "div"}
        href={href}
        {...rest}
      />
    </IsPickedContext.Provider>
  );
};
LinearTileV2.Base = Base;

const IsPickedContext = createContext<boolean | undefined>(false);

const StandardIndicator = ({
  className,
  omitText,
  ...props
}: Omit<JSX.IntrinsicElements["div"], "children"> & { omitText?: boolean }) => {
  const { t } = useTranslation();
  return (
    <div className={classNames(classes.standardIndicator, className)} {...props}>
      <Icon type={IconType.Star} />
      {!omitText && <span>{t("configurator.standard")}</span>}
    </div>
  );
};
LinearTileV2.StandardIndicator = StandardIndicator;

const Image = ({ tKey = "", ratio, src }: { tKey?: string; src?: string; ratio: string }) => {
  const { t } = useXmlConfiguratorTranslations();
  const imgSrc = src ?? t?.(tKey) ?? tKey;
  return <img className={classes.image} style={{ aspectRatio: ratio }} src={imgSrc} alt="" />;
};
LinearTileV2.Image = Image;

const CheckedIndicator = () => {
  const isPicked = useContext(IsPickedContext);
  return <CheckedCircle className={classes.checkedIndicator} checked={isPicked} />;
};
LinearTileV2.CheckedIndicator = CheckedIndicator;

function renderContent(content: ReactNode) {
  return typeof content === "string" ? (
    <LanguageAwareExternalContent content={content} loadedClassname={"scha__lc__external-label"} />
  ) : (
    content
  );
}

const Headline = ({ content, children }: { content: ReactNode; children?: ReactNode }) => (
  <p className={classes.headline}>
    {renderContent(content)}
    {children}
  </p>
);
LinearTileV2.Headline = Headline;

const SubHeadline = ({ content, children }: { content: ReactNode; children?: ReactNode }) => (
  <p className={classes.subHeadline}>
    {renderContent(content)}
    {children}
  </p>
);
LinearTileV2.SubHeadline = SubHeadline;
LinearTileV2.Info = LinearInfo;
